import React, { useState, useRef, useCallback } from 'react';
import { Card, Tabs, Button, Space, List, Typography, Tag, Select, Input, Row, Col, Alert, Divider } from 'antd';
import { PlusOutlined, RobotOutlined, ThunderboltOutlined, PlayCircleOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const presetWorkflows = {
  inventory: [
    { name: 'Reorder Low-Stock Items', desc: 'Auto-create PO when stock drops below threshold', tag: 'Auto-PO' },
    { name: 'Quality Hold Routing', desc: 'Route received items with discrepancies to QA queue', tag: 'QA' },
  ],
  sales: [
    { name: 'Abandoned Quote Follow-up', desc: 'Send reminder for quotes not converted in 7 days', tag: 'Follow-up' },
    { name: 'VIP Order Escalation', desc: 'Notify team when high-value order is placed', tag: 'Alert' },
  ],
  purchasing: [
    { name: 'Vendor SLA Breach Alert', desc: 'Flag POs past expected date', tag: 'SLA' },
    { name: 'Auto-Receive Suggestions', desc: 'Suggest receiving based on ASN/expected date', tag: 'Assist' },
  ],
};

// Mini n8n-like flow builder
const nodeColors = {
  trigger: '#0ea5e9',
  condition: '#f59e0b',
  action: '#10b981',
};

const FlowBuilder = ({ domain }) => {
  const [nodes, setNodes] = useState([
    { id: 'n1', type: 'trigger', label: 'Trigger', x: 60, y: 60 },
    { id: 'n2', type: 'action', label: 'Action', x: 320, y: 60 },
  ]);
  const [edges, setEdges] = useState([{ id: 'e1', from: 'n1', to: 'n2' }]);
  const [drag, setDrag] = useState({ id: null, dx: 0, dy: 0 });
  const [connectingFrom, setConnectingFrom] = useState(null);
  const canvasRef = useRef(null);

  const onMouseMove = useCallback((e) => {
    if (!drag.id) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - drag.dx;
    const y = e.clientY - rect.top - drag.dy;
    setNodes((ns) => ns.map((n) => (n.id === drag.id ? { ...n, x, y } : n)));
  }, [drag]);

  const onMouseUp = () => setDrag({ id: null, dx: 0, dy: 0 });

  const startDrag = (id, e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const node = nodes.find((n) => n.id === id);
    setDrag({ id, dx: e.clientX - rect.left - node.x, dy: e.clientY - rect.top - node.y });
  };

  const addNode = (type) => {
    const id = `n${Date.now()}`;
    const x = 80 + Math.random() * 240;
    const y = 120 + Math.random() * 160;
    const label = type.charAt(0).toUpperCase() + type.slice(1);
    setNodes((ns) => [...ns, { id, type, label, x, y }]);
  };

  const removeNode = (id) => {
    setNodes((ns) => ns.filter((n) => n.id !== id));
    setEdges((es) => es.filter((e) => e.from !== id && e.to !== id));
  };

  const addEdge = (from, to) => {
    if (!from || !to || from === to) return;
    const id = `e${Date.now()}`;
    // prevent duplicates
    setEdges((es) => (es.some((e) => e.from === from && e.to === to) ? es : [...es, { id, from, to }]));
  };

  const pos = (id) => nodes.find((n) => n.id === id);

  return (
    <div className="space-y-3">
      <Alert
        message={`Automation (${domain})`}
        description="Drag nodes, connect with handles. Use AI Assist to propose a starting flow."
        type="info"
        showIcon
      />
      <Card size="small">
        <Space wrap>
          <Button icon={<PlusOutlined />} size="small" onClick={() => addNode('trigger')}>Add Trigger</Button>
          <Button icon={<PlusOutlined />} size="small" onClick={() => addNode('condition')}>Add Condition</Button>
          <Button icon={<PlusOutlined />} size="small" type="primary" onClick={() => addNode('action')}>Add Action</Button>
          <Divider type="vertical" />
          <Button size="small" icon={<RobotOutlined />}>AI Assist</Button>
          <Button size="small" type="primary" icon={<PlayCircleOutlined />}>Enable</Button>
        </Space>
      </Card>

      <Card size="small" styles={{ body: { padding: 0 } }}>
        <div
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          style={{ position: 'relative', height: 520, background: 'linear-gradient(180deg,#1e293b 0,#1e293b 0)'}}
          className="bg-slate-50 dark:bg-slate-800 overflow-hidden rounded"
        >
          {/* Edges as SVG */}
          <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {edges.map((e) => {
              const a = pos(e.from); const b = pos(e.to);
              if (!a || !b) return null;
              const x1 = a.x + 160; const y1 = a.y + 24; // right handle
              const x2 = b.x; const y2 = b.y + 24;       // left handle
              const mx = (x1 + x2) / 2;
              const d = `M ${x1},${y1} C ${mx},${y1} ${mx},${y2} ${x2},${y2}`;
              return <path key={e.id} d={d} stroke="#94a3b8" strokeWidth={2} fill="none" />;
            })}
            {connectingFrom && (() => {
              const a = pos(connectingFrom);
              if (!a) return null;
              return null; // live preview omitted for simplicity
            })()}
          </svg>

          {/* Nodes */}
          {nodes.map((n) => (
            <div
              key={n.id}
              style={{ position: 'absolute', left: n.x, top: n.y, width: 160 }}
              className="select-none"
            >
              <div
                className="rounded shadow border dark:border-slate-700"
                style={{ background: '#ffffff', borderTop: `4px solid ${nodeColors[n.type]}` }}
              >
                <div
                  onMouseDown={(e) => startDrag(n.id, e)}
                  className="cursor-move px-3 py-2 text-xs font-medium bg-gray-50 dark:bg-slate-800 flex items-center justify-between"
                >
                  <span>{n.label}</span>
                  <Button size="small" type="text" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); removeNode(n.id); }} />
                </div>
                <div className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                  <Input size="small" placeholder="Description" />
                </div>
              </div>

              {/* Left handle (in) */}
              <div
                title="Connect from..."
                style={{ position: 'absolute', left: -6, top: 20, width: 12, height: 12, borderRadius: 9999, background: '#64748b', cursor: 'pointer' }}
                onClick={() => connectingFrom ? (addEdge(connectingFrom, n.id), setConnectingFrom(null)) : null}
              />
              {/* Right handle (out) */}
              <div
                title="Connect to..."
                style={{ position: 'absolute', right: -6, top: 20, width: 12, height: 12, borderRadius: 9999, background: nodeColors[n.type], cursor: 'pointer' }}
                onClick={() => setConnectingFrom(n.id)}
              />
            </div>
          ))}
        </div>
      </Card>

      <Card size="small" title="Templates">
        <List
          dataSource={presetWorkflows[domain]}
          renderItem={(wf) => (
            <List.Item actions={[<Button size="small">Use</Button>] }>
              <List.Item.Meta
                title={<span className="text-sm">{wf.name} <Tag color="blue">{wf.tag}</Tag></span>}
                description={<Text type="secondary" className="text-xs">{wf.desc}</Text>}
              />
            </List.Item>
          )}
        />
      </Card>
    </div>
  );
};

const Automation = () => {
  const items = [
    { key: 'inventory', label: 'Inventory', children: <FlowBuilder domain="inventory" /> },
    { key: 'sales', label: 'Sales', children: <FlowBuilder domain="sales" /> },
    { key: 'purchasing', label: 'Purchasing', children: <FlowBuilder domain="purchasing" /> },
  ];

  return (
    <div className="space-y-6">
      <div>
        <Title level={3} style={{ marginBottom: 0 }}>Automation</Title>
        <Text type="secondary">Create AI-powered workflows without code</Text>
      </div>

      <Card>
        <Tabs defaultActiveKey="inventory" items={items} />
      </Card>
    </div>
  );
};

export default Automation;
